/** Shared Schedule of Activities table model for Word + native PDF export. */

import { resolveInheritedSoaColumns } from '../protocol/sections/s9-assessment-schedule/utils/resolveSoaTableColumns.js';

export function stripFootnoteNumberForSoaExport(text) {
  if (!text) return '';
  return String(text)
    // Leading "1. " / "1) " / "a. " / "a) " / "[a] " / "(1) " / "A." 
    .replace(/^\s*[\[\(]?\d+[\]\)]?[\.\):]\s*/u, '')
    .replace(/^\s*[\[\(]?[a-zA-Z]{1,3}[\]\)]?[\.\):]\s*/u, '')
    .replace(/^\s*[a-zA-Z]{1,3}\s*[-–—]\s*/u, '')
    // Trailing / embedded power-style markers like "¹" or " ^1"
    .replace(/\s*[\^⁺]?[¹²³⁴⁵⁶⁷⁸⁹⁰]+\s*$/u, '')
    .replace(/\s*\^\d+\s*$/g, '')
    .trim();
}

function classifySoaFootnoteLine(line) {
  const s = String(line).trim();
  const low = s.toLowerCase();
  if (/^(general|note:)/i.test(s) || /^overall\b/i.test(low)) return 'general';
  if (/^(source|sources|references?|retrieved|doi:|http)/i.test(s)) return 'source';
  if (/^(abbrev|abbreviation)/i.test(low)) return 'abbrev';
  return 'letter';
}

/** Order notes for display under each schedule (no letter/number labels added). */
export function orderSoaFootnoteLines(footnoteStrings) {
  const list = (footnoteStrings || [])
    .map((x) => stripFootnoteNumberForSoaExport(x))
    .filter(Boolean);
  const buckets = { general: [], letter: [], abbrev: [], source: [] };
  list.forEach((line) => {
    buckets[classifySoaFootnoteLine(line)].push(line);
  });
  return [...buckets.general, ...buckets.letter, ...buckets.abbrev, ...buckets.source];
}

/** @deprecated Prefer plain Key Notes lines without letter markers. */
export function footnoteLetterLabel(indexZeroBased) {
  let n = indexZeroBased;
  let s = '';
  do {
    s = String.fromCharCode(97 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

/** Normalize headings to "1.3 Schedule of Activities" (protocol SoA), keep explicit leading number if present. */
export function normalizeScheduleOfActivitiesHeading(heading) {
  const s = String(heading || '').trim();
  const num = s.match(/^(\d+(?:\.\d+)*)\b/);
  if (num) {
    // Protocol SoA is 1.3 — never keep a bare "9" from the old S9 module label
    const n = num[1];
    if (n === '9' || n.startsWith('9.')) {
      return '1.3 Schedule of Activities';
    }
    return `${n} Schedule of Activities`;
  }
  return '1.3 Schedule of Activities';
}

function buildS9ColumnKey(col) {
  const parts = [col?.l1, col?.l2, col?.l3, col?.l4]
    .map((p) => (p == null || p === '' ? '' : String(p).trim()))
    .filter((p) => p.length > 0);
  return parts.join('|');
}

function s9ColumnLabel(col) {
  if (!col || typeof col !== 'object') return { period: '', day: '' };
  const period =
    String(col.headerTier1 || col.l1 || col.period || '').trim() ||
    String(col.l2 || '').trim() ||
    'Visit';
  const dayPartsRaw = [col.headerTier2 || col.l2, col.headerTier3 || col.l3, col.l4, col.window]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter(Boolean);
  // Source columns often carry overlapping window/day text across more than one of these
  // fields — sometimes an exact repeat (both headerTier2 and window hold "-28 to ~-1 days"),
  // sometimes the `window` field is just the +/- tolerance already embedded in headerTier3
  // (e.g. headerTier3 "Day 1 (+/- 2 days)" and window "+/- 2 days" — not identical strings,
  // but window adds nothing since it's already a substring of what's shown). Drop any part
  // that's an exact duplicate OR fully contained in a part already kept — without this, real
  // SOA headers were routinely showing "Day 1 (+/- 2 days) / +/- 2 days", inflating an already
  // narrow, multi-column-sharing cell into several extra wrapped lines for no new information.
  const accepted = [];
  const dayParts = dayPartsRaw.filter((p) => {
    const key = p.toLowerCase();
    const isRedundant = accepted.some((a) => {
      const acceptedKey = a.toLowerCase();
      return acceptedKey === key || acceptedKey.includes(key) || key.includes(acceptedKey);
    });
    if (isRedundant) return false;
    accepted.push(p);
    return true;
  });
  const day =
    dayParts.filter((p, i) => !(i === 0 && p === period)).join(' / ') ||
    String(col.day || '').trim() ||
    '—';
  return { period, day };
}

function normalizeS9MarkValue(value) {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'X' : '';
  const s = String(value).trim();
  if (!s || s === '0' || /^false$/i.test(s)) return '';
  return s;
}

/** Convert one S9 chat table → legacy payload. Footnotes linked to an activity travel with
 * that activity (`footnoteTexts`, plain text, no letter/number label) so they render under
 * that activity's row rather than power-style superscripts; only unlinked notes stay in the
 * table-level `footnotes` block.
 * @param {object} table
 * @param {object[]} [allTables] - sibling S9 tables, needed to resolve a table whose own
 *   `columns` is empty and relies on `inheritsFromTableId` (e.g. T2 inheriting T1's visit
 *   columns) — mirrors what the live S9 chat renderer (SoaTableRenderer.jsx) does via
 *   resolveSoaTableForRender before display, so export doesn't lose visit columns/X-marks
 *   that are visible on screen. */
export function s9TableToLegacyPayload(table, allTables) {
  const columns = resolveInheritedSoaColumns(table, allTables);
  const rowGroups = Array.isArray(table?.rowGroups) ? table.rowGroups : [];
  const grid = Array.isArray(table?.grid) ? table.grid : [];
  const footnotesRaw = Array.isArray(table?.footnotes) ? table.footnotes : [];

  const columnKeys = columns.map((c, idx) => buildS9ColumnKey(c) || `col-${idx}`);
  const visits = columns.map((col) => s9ColumnLabel(col));

  const footnoteTextById = new Map();
  const footnoteEntries = [];
  footnotesRaw.forEach((fn, i) => {
    if (!fn || typeof fn !== 'object') return;
    const id = String(fn.id ?? i + 1).trim();
    const text = stripFootnoteNumberForSoaExport(fn.text ?? '');
    if (!id || !text) return;
    footnoteTextById.set(id, text);
    footnoteEntries.push({ id, text });
  });

  const referencedFootnoteIds = new Set();
  const collectNoteIds = (rawRefs) => {
    const ids = [];
    const seen = new Set();
    rawRefs.forEach((ref) => {
      const id = String(ref ?? '').trim();
      if (!id || seen.has(id) || !footnoteTextById.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    return ids;
  };

  const marksByAssessment = new Map();
  grid.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const name = String(row.assessment || row.name || '').trim();
    if (!name) return;
    const marks = Array.isArray(row.marks) ? row.marks : [];
    const visitIndices = [];
    const noteRefs = [];
    marks.forEach((m) => {
      const ck = String(m?.columnKey || '').trim();
      const val = normalizeS9MarkValue(m?.value);
      if (!val) return;
      const idx = columnKeys.indexOf(ck);
      if (idx >= 0) visitIndices.push(idx);
      if (m?.noteRef != null) noteRefs.push(m.noteRef);
    });
    marksByAssessment.set(name, { visitIndices: [...new Set(visitIndices)], noteRefs });
  });

  const activityFootnoteTexts = (fromGrid, a) => {
    const rawRefs = [...(fromGrid?.noteRefs || [])];
    if (typeof a === 'object' && a) {
      if (Array.isArray(a.footnoteRefs)) rawRefs.push(...a.footnoteRefs);
      if (Array.isArray(a.noteRefs)) rawRefs.push(...a.noteRefs);
      if (a.footnoteRef != null) rawRefs.push(a.footnoteRef);
      if (a.noteRef != null) rawRefs.push(a.noteRef);
    }
    const noteIds = collectNoteIds(rawRefs);
    noteIds.forEach((id) => referencedFootnoteIds.add(id));
    return noteIds.map((id) => footnoteTextById.get(id)).filter(Boolean);
  };

  let sections = rowGroups.map((group) => {
    const title = String(group?.group || group?.title || 'Assessments').trim() || 'Assessments';
    const assessments = Array.isArray(group?.assessments) ? group.assessments : [];
    const activities = assessments.map((a) => {
      const name =
        typeof a === 'string'
          ? a
          : String(a?.name || a?.assessment || a?.label || '').trim();
      const fromGrid = marksByAssessment.get(name) || { visitIndices: [], noteRefs: [] };
      const footnoteTexts = activityFootnoteTexts(fromGrid, a);
      return {
        name: name || 'Assessment',
        visits: fromGrid.visitIndices,
        ...(footnoteTexts.length ? { footnoteTexts } : {}),
      };
    });
    return { title, activities };
  });

  // Chat SOA sometimes has grid marks before rowGroups are filled — still export rows.
  if (!sections.length && marksByAssessment.size > 0) {
    sections = [
      {
        title: String(table?.title || table?.name || 'Assessments').trim() || 'Assessments',
        activities: [...marksByAssessment.entries()].map(([name, fromGrid]) => {
          const footnoteTexts = activityFootnoteTexts(fromGrid, null);
          return {
            name,
            visits: fromGrid.visitIndices,
            ...(footnoteTexts.length ? { footnoteTexts } : {}),
          };
        }),
      },
    ];
  }

  // Only notes not linked to any activity stay in the table-level block.
  const footnotes = footnoteEntries
    .filter(({ id }) => !referencedFootnoteIds.has(id))
    .map(({ text }) => text);

  return {
    visits,
    sections,
    footnotes,
    summary: 'Schedule of Activities',
    sourceTableId: table?.tableId || null,
  };
}

// "Tx C1 D1", "C2D15", "Cycle 3+ Day 1" → Treatment / Cycle N / Day M header tiers.
const CYCLE_DAY_VISIT_RE = /^(?:tx|treatment)?\s*c(?:ycle)?\s*(\d+\+?)\s*[,/-]?\s*d(?:ay)?\s*(-?\d+)$/i;

/**
 * Inverse of s9TableToLegacyPayload: legacy {visits, sections, footnotes} → one S9 table
 * ({tableId, columns, rowGroups, grid, footnotes}) so flat SoA data (e.g. rebuilt from a
 * USDM ScheduleTimeline with no extension:soaGrid) renders through SoaTableRenderer with
 * the same grouped / tiered-header look as S9-authored tables.
 * @returns {object|null} null when there is nothing to render.
 */
export function legacySoaPayloadToS9Table(payload, tableId = 'T1') {
  const visits = Array.isArray(payload?.visits) ? payload.visits : [];
  const sections = Array.isArray(payload?.sections) ? payload.sections : [];
  if (!visits.length || !sections.length) return null;

  const seenKeys = new Set();
  const columns = visits.map((v, idx) => {
    const period = String(v?.period ?? v?.name ?? '').trim() || `Visit ${idx + 1}`;
    const day = String(v?.day ?? '').trim();
    const window = String(v?.window ?? '').trim();
    const cycleDay = !day ? period.match(CYCLE_DAY_VISIT_RE) : null;
    const col = cycleDay
      ? { l1: 'Treatment', l2: `Cycle ${cycleDay[1]}`, l3: `Day ${cycleDay[2]}` }
      : { l1: period, ...(day ? { l3: day } : {}) };
    if (window) col.window = window;
    // Grid marks join on the l1–l4 key, so it must be unique per column.
    if (seenKeys.has(buildS9ColumnKey(col))) col.l4 = `#${idx + 1}`;
    seenKeys.add(buildS9ColumnKey(col));
    return col;
  });

  // Label stripping ("1.", "a)") is left to SoaTableRenderer's own stripFootnoteLabel.
  const footnotes = (payload.footnotes || [])
    .map((text, i) => ({ id: String(i + 1), text: String(text ?? '').trim() }))
    .filter((fn) => fn.text);
  const footnoteIdByIndex = new Map(
    (payload.footnotes || []).map((_, i) => [i, String(i + 1)]),
  );

  const usedNames = new Map();
  const grid = [];
  const rowGroups = sections.map((section) => {
    const assessments = (Array.isArray(section?.activities) ? section.activities : []).map((act) => {
      let name = String(act?.name || '').trim() || 'Activity';
      // Marks are looked up by assessment name — keep names unique within the table.
      const n = (usedNames.get(name) || 0) + 1;
      usedNames.set(name, n);
      if (n > 1) name = `${name} (${n})`;

      const footnoteRefs = [];
      const powerIdx = Number(act?.power) - 1;
      if (footnoteIdByIndex.has(powerIdx)) footnoteRefs.push(footnoteIdByIndex.get(powerIdx));
      (Array.isArray(act?.footnoteTexts) ? act.footnoteTexts : []).forEach((raw) => {
        const text = String(raw ?? '').trim();
        if (!text) return;
        const id = `n${footnotes.length + 1}`;
        footnotes.push({ id, text });
        footnoteRefs.push(id);
      });

      const visitIdx = [...new Set((Array.isArray(act?.visits) ? act.visits : []).map(Number))]
        .filter((i) => Number.isInteger(i) && i >= 0 && i < columns.length);
      grid.push({
        assessment: name,
        marks: visitIdx.map((i) => ({ columnKey: buildS9ColumnKey(columns[i]), value: 'X' })),
      });
      return { name, ...(footnoteRefs.length ? { footnoteRefs } : {}) };
    });
    return { group: String(section?.title || '').trim() || 'Assessments', assessments };
  });

  return {
    tableId,
    label: String(payload?.summary || 'Schedule of Activities'),
    columns,
    rowGroups,
    grid,
    footnotes,
  };
}

/**
 * One legacy payload per schedule when S9 multi-table data exists.
 * Key Notes stay under each schedule (not merged at document end).
 */
export function listSoaLegacyPayloadsForExport(data) {
  const s9Tables = Array.isArray(data?.s9?.tables)
    ? data.s9.tables
    : Array.isArray(data?.tables)
      ? data.tables
      : [];
  if (s9Tables.length > 0) {
    return s9Tables
      .map((t) => s9TableToLegacyPayload(t, s9Tables))
      .filter((p) => p.visits?.length || p.sections?.length);
  }
  if (data?.visits && data?.sections) {
    return [
      {
        visits: data.visits,
        sections: data.sections,
        footnotes: (data.footnotes || []).map(stripFootnoteNumberForSoaExport).filter(Boolean),
        summary: 'Schedule of Activities',
      },
    ];
  }
  return [];
}

/**
 * Transform SOA payload into a flat table model.
 * Visit marks are plain X only (no power/superscript).
 * Each activity's own linked footnote text (if any) fills its Key Notes cell — plain prose,
 * no letter/number label. Notes not linked to any activity still appear once under the
 * schedule (footnotesBlock).
 * @param {object} data
 * @param {{ includeKeyNotesColumn?: boolean }} [options]
 */
export function soaDataToTableRows(data, options = {}) {
  if (!data || !data.visits || !data.sections) {
    return { header: [], rows: [], footnotesBlock: [] };
  }

  const includeKeyNotesColumn = options.includeKeyNotesColumn !== false;
  const footnotes = (data.footnotes || []).map(stripFootnoteNumberForSoaExport);

  /** Legacy (non-S9) activities may carry a 1-based `power` index into `footnotes` instead of
   * pre-resolved `footnoteTexts` — support both so the Key Notes cell fills either way. */
  const referencedFootnoteIdx = new Set();
  data.sections.forEach((section) => {
    (section.activities || []).forEach((activity) => {
      const idx = Number(activity?.power) - 1;
      if (Number.isInteger(idx) && idx >= 0 && idx < footnotes.length) {
        referencedFootnoteIdx.add(idx);
      }
    });
  });

  const activityNoteTexts = (activity) => {
    if (Array.isArray(activity?.footnoteTexts) && activity.footnoteTexts.length) {
      return activity.footnoteTexts;
    }
    const idx = Number(activity?.power) - 1;
    return Number.isInteger(idx) && idx >= 0 && idx < footnotes.length && footnotes[idx]
      ? [footnotes[idx]]
      : [];
  };

  const rawVisitLabels = data.visits.map((visit) => {
    const period = String(visit.period || '').trim();
    const day = String(visit.day || '').trim();
    // Full text, no truncation — the Word table uses a FIXED layout with explicit
    // columnWidths (getSoaColumnWidths), so long headers wrap onto extra lines within
    // their own column instead of overlapping neighboring columns. Truncating here used
    // to silently cut off real header content (e.g. "Subsequent Cycles (Every 21 days)"
    // → "Subsequent Cycles (Every 21…") for no actual layout benefit.
    if (period && day && period !== day) {
      return `${period}\n${day}`;
    }
    return period || day || '—';
  });

  // Every consumer of this model (Word + PDF row/column lookups, isSoaSectionBannerRow,
  // chunkSoaTableRows' per-row estimates) keys a row's cell data by the header LABEL TEXT,
  // not by column index. Two visits that render the same label (most commonly both falling
  // back to the bare '—' placeholder above) previously collided on that key: the second
  // visit's activityRow[col] write silently overwrote the first's, so one of the two columns
  // showed the wrong (or the other column's) data — visually indistinguishable from a
  // "missing" column. Disambiguate with the 1-based visit position so every header entry is
  // a guaranteed-unique lookup key; this keeps every downstream consumer working unchanged.
  const seenVisitLabelCounts = new Map();
  const visitLabels = rawVisitLabels.map((label, idx) => {
    const count = (seenVisitLabelCounts.get(label) || 0) + 1;
    seenVisitLabelCounts.set(label, count);
    return count === 1 ? label : `${label} (${idx + 1})`;
  });

  const header = includeKeyNotesColumn
    ? ['Study procedures and visits', ...visitLabels, 'Key Notes']
    : ['Study procedures and visits', ...visitLabels];

  const rows = [];
  const lastCol = header.length - 1;

  data.sections.forEach((section) => {
    const sectionRow = {};
    header.forEach((col, i) => {
      if (includeKeyNotesColumn && i === lastCol) sectionRow[col] = '';
      else sectionRow[col] = i === 0 ? section.title || '' : '';
    });
    rows.push(sectionRow);

    if (section.activities && Array.isArray(section.activities)) {
      section.activities.forEach((activity) => {
        const activityRow = {};
        const noteTexts = activityNoteTexts(activity);
        header.forEach((col, i) => {
          if (i === 0) {
            activityRow[col] = activity.name || '';
          } else if (includeKeyNotesColumn && i === lastCol) {
            activityRow[col] = noteTexts.join('\n');
          } else {
            const visitIndex = i - 1;
            activityRow[col] =
              activity.visits && activity.visits.includes(visitIndex) ? 'X' : '';
          }
        });
        // Always attached (independent of includeKeyNotesColumn) so callers that render
        // Key Notes inline under the activity — rather than as a dedicated column — have
        // the text available without needing the column present in `header`.
        if (noteTexts.length) activityRow._keyNotesText = noteTexts.join('\n');
        rows.push(activityRow);
      });
    }
  });

  const generalFootnotes = footnotes.filter((_, idx) => !referencedFootnoteIdx.has(idx));
  const footnotesBlock = generalFootnotes.length > 0 ? orderSoaFootnoteLines(generalFootnotes) : [];
  // Raw (period, day) per visit column, aligned 1:1 with visitLabels/header's visit entries —
  // lets the PDF renderer build a grouped 2-tier header (one shared "period" cell spanning
  // consecutive same-period columns, e.g. Cycle 1's Day 1/Day 15) instead of repeating the
  // full "period\nday" text in every narrow column, which is what was making SOA headers so
  // tall. `header`/`rows` stay flat text for every other consumer (Word, row-data keys).
  const visitMeta = data.visits.map((visit) => ({
    period: String(visit.period || '').trim(),
    day: String(visit.day || '').trim(),
  }));
  return { header, rows, footnotesBlock, visitMeta };
}

/**
 * Group consecutive visit columns that share the same non-empty period into one span, for a
 * grouped 2-tier PDF header (shared period cell over its day sub-columns). A visit with no
 * period, or a period that differs from its predecessor, starts its own single-column group.
 * @param {{period: string, day: string}[]} visitMeta
 */
export function buildSoaHeaderGroups(visitMeta) {
  const groups = [];
  (visitMeta || []).forEach((visit) => {
    const period = String(visit?.period || '').trim();
    const day = String(visit?.day || '').trim();
    const prev = groups[groups.length - 1];
    if (period && prev && prev.period === period) {
      prev.span += 1;
      prev.days.push(day);
    } else {
      groups.push({ period, span: 1, days: [day] });
    }
  });
  return groups;
}

export function isSoaSectionBannerRow(rowData, header) {
  if (!header?.length) return false;
  const hasKeyNotes = header[header.length - 1] === 'Key Notes';
  const midKeys = hasKeyNotes ? header.slice(1, -1) : header.slice(1);
  return (
    midKeys.every((key) => !String(rowData[key] ?? '').trim()) &&
    String(rowData[header[0]] ?? '').trim().length > 0
  );
}

/** Landscape A4 printable width (pt) — matches SOA PDF page horizontal padding (48 pt). */
export const SOA_PDF_USABLE_WIDTH_PT = 842 - 48 * 2;

/** Minimum width for a visit/day column before we split the table horizontally. */
export const SOA_PDF_MIN_VISIT_COL_PT = 64;

/**
 * Split a wide SOA table into parts (row label repeated) so visit columns stay readable.
 */
export function splitSoaTableForPdfWidth(header, rows, usableWidth = SOA_PDF_USABLE_WIDTH_PT, visitMeta = null) {
  if (!header?.length) return [];
  const hasKeyNotes = header[header.length - 1] === 'Key Notes';
  const labelKey = header[0];
  const keyNotesKey = hasKeyNotes ? header[header.length - 1] : null;
  const visitKeys = hasKeyNotes ? header.slice(1, -1) : header.slice(1);

  // Key Notes cells routinely carry a full sentence of footnote text (e.g. "Informed
  // Consent must be obtained before any study-specific procedures are performed.").
  // At the old 8% share (~60pt of a 746pt landscape page) that wraps into 7-8 lines,
  // inflating the row's estimated/rendered height ~5x — which starves chunkSoaTableRows'
  // page budget and strands that single row alone on its own page. Giving Key Notes a
  // larger, and the label column a slightly smaller, share keeps typical footnote rows
  // to 2-3 lines instead.
  const labelW = Math.round(usableWidth * 0.24);
  const keyNotesW = hasKeyNotes ? Math.round(usableWidth * 0.18) : 0;
  const available = Math.max(80, usableWidth - labelW - keyNotesW);
  const maxVisits = Math.max(3, Math.floor(available / SOA_PDF_MIN_VISIT_COL_PT));

  if (visitKeys.length <= maxVisits) {
    return [{ header, rows, visitMeta: visitMeta || null }];
  }

    // Balance columns across parts (avoid a tiny final part like 8+1).
  const partCount = Math.ceil(visitKeys.length / maxVisits);
  const perPart = Math.ceil(visitKeys.length / partCount);

  const parts = [];
  for (let i = 0; i < visitKeys.length; i += perPart) {
    const slice = visitKeys.slice(i, i + perPart);
    const partHeader = hasKeyNotes
      ? [labelKey, ...slice, keyNotesKey]
      : [labelKey, ...slice];
    const partRows = (rows || []).map((row) => {
      // _keyNotesText (Key Notes rendered inline under the activity, not as a column) isn't
      // one of partHeader's keys, so it must be copied explicitly — otherwise every note is
      // silently dropped whenever a table is wide enough to need splitting into parts.
      const next = { _soaContinuation: row._soaContinuation, _keyNotesText: row._keyNotesText };
      partHeader.forEach((key) => {
        next[key] = row[key] ?? '';
      });
      return next;
    });
    const partVisitMeta = visitMeta ? visitMeta.slice(i, i + perPart) : null;
    parts.push({ header: partHeader, rows: partRows, visitMeta: partVisitMeta });
  }
  return parts;
}

/**
 * Column widths (pt) — label column wider; visit columns share the rest evenly.
 * Widths always sum to exactly usableWidth; every width is >= 2 pt.
 * @param {number} columnCount
 * @param {number} [usableWidth]
 * @param {{ hasKeyNotesColumn?: boolean }} [options]
 */
export function getSoaPdfColumnWidths(
  columnCount,
  usableWidth = SOA_PDF_USABLE_WIDTH_PT,
  options = {},
) {
  const n = Number(columnCount) || 0;
  const total = Math.max(1, Number(usableWidth) || SOA_PDF_USABLE_WIDTH_PT);
  const hasKeyNotesColumn = options.hasKeyNotesColumn === true;
  if (n <= 0) return [];
  if (n === 1) return [total];
  if (n === 2) {
    const first = Math.round(total * 0.55);
    return [first, total - first];
  }

  if (!hasKeyNotesColumn) {
    // Label + visit columns only (PDF omits empty Key Notes column).
    const first = Math.round(total * 0.24);
    const rest = total - first;
    const visitCount = n - 1;
    const per = Math.max(SOA_PDF_MIN_VISIT_COL_PT, Math.floor((rest / visitCount) * 100) / 100);
    const widths = [first, ...Array.from({ length: visitCount }, () => per)];
    // If forced min made us overflow, scale visits down (caller should have split).
    let sum = widths.reduce((a, b) => a + b, 0);
    if (sum > total + 0.01) {
      const scale = (total - first) / (sum - first);
      for (let i = 1; i < n; i += 1) {
        widths[i] = Math.max(2, Math.round(widths[i] * scale * 100) / 100);
      }
      sum = widths.reduce((a, b) => a + b, 0);
    }
    widths[n - 1] = Math.max(2, Math.round((widths[n - 1] + (total - sum)) * 100) / 100);
    return widths;
  }

  const middleCount = n - 2;
  // Kept in sync with splitSoaTableForPdfWidth's labelW/keyNotesW shares above — see
  // that comment for why Key Notes needs more than a token 8% share.
  let first = Math.round(total * 0.24);
  let last = Math.round(total * 0.18);
  let remaining = total - first - last;

  const minMiddleTotal = middleCount * SOA_PDF_MIN_VISIT_COL_PT;
  if (remaining < minMiddleTotal) {
    const minMid = middleCount * 2;
    first = Math.max(40, Math.round((total - minMid) * 0.7));
    last = Math.max(20, total - minMid - first);
    remaining = total - first - last;
  }

  const perMiddle = Math.max(2, Math.floor(remaining / middleCount));
  const widths = [first];
  let used = first;
  for (let i = 0; i < middleCount; i += 1) {
    const w = i === middleCount - 1 ? total - last - used : perMiddle;
    widths.push(Math.max(2, w));
    used += widths[widths.length - 1];
  }
  widths.push(Math.max(2, total - used));

  const sum = widths.reduce((a, b) => a + b, 0);
  if (Math.abs(sum - total) > 0.01) {
    widths[widths.length - 1] = Math.max(2, widths[widths.length - 1] + (total - sum));
  }
  return widths.map((w) => Math.max(2, Math.round(w * 100) / 100));
}

/** Scale visit-column font down when many columns must fit on one landscape page. */
export function getSoaPdfFontSizes(columnCount, fontConfig = null) {
  const visitCols = Math.max(0, (columnCount || 0) - 2);
  const headerPt = Number(fontConfig?.tableHeader?.size) || 10;
  const bodyPt = Number(fontConfig?.tableCell?.size) || 10;
  const footnotePt = Number(fontConfig?.footnote?.size) || 9;

  let visitHeaderPt = headerPt;
  if (visitCols > 10) visitHeaderPt = 7;
  else if (visitCols > 8) visitHeaderPt = 8;
  else if (visitCols > 6) visitHeaderPt = 9;

  const clamp = (n, min = 7, max = 12) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return min;
    return Math.min(max, Math.max(min, v));
  };

  return {
    headerPt: clamp(headerPt, 8, 12),
    bodyPt: clamp(bodyPt, 8, 12),
    visitHeaderPt: clamp(visitHeaderPt, 7, 11),
    visitBodyPt: clamp(Math.min(bodyPt, visitHeaderPt + 1), 8, 11),
    footnotePt: clamp(footnotePt, 7, 11),
  };
}
